import { useIntl } from 'react-intl';
import { Row, Col, Form, Select, FormInstance, Checkbox } from 'antd';

import { useAddressQuery } from '@/hooks';

import FloatingFormItem from '@/components/_common/FloatingFormItem';

import { ReportModalEmployeeFormValue } from '@/core/domain/reports/people/Report.types';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useAreas } from '@/core/domain/operational/Areas/Areas.hooks';
import {
  CompaniesInfiniteSelectField,
  InputDecimal,
  RangePicker,
} from '@/components/_common';
import FunctionsSelect from '@/components/_common/FunctionsSelect';

interface Props {
  form?: FormInstance<ReportModalEmployeeFormValue>;
}

function EmployeeReportForm({ form }: Props) {
  const intl = useIntl();

  const { state, city } = useAddressQuery();

  const { data: areasData, isFetching: isFetchingAreas } = useAreas({
    active: true,
  });

  const areas = flatInfiniteData(areasData);

  const handleFormsValuesChange = (
    values: ReportModalEmployeeFormValue,
    fieldsValue: ReportModalEmployeeFormValue,
  ) => {
    handleStateChange(values);

    if (!fieldsValue.uf) {
      form?.resetFields(['city']);
      state.setSelectedStateCode('');
    }
  };

  function handleStateChange(values: ReportModalEmployeeFormValue) {
    if (!values.uf) return;

    form?.resetFields(['city']);

    state.setSelectedStateCode(values.uf);
  }

  const specificDate = Form.useWatch('specificDate', form);

  return (
    <Form
      layout="vertical"
      form={form}
      onValuesChange={handleFormsValuesChange}
    >
      <Row gutter={[8, 8]}>
        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.professional.form.situation',
            })}
            name="active"
          >
            <Select
              allowClear
              style={{ width: '100%' }}
              options={[
                {
                  value: true,
                  label: intl.formatMessage({
                    id: 'employee.filters.active.options.active',
                  }),
                },
                {
                  value: false,
                  label: intl.formatMessage({
                    id: 'employee.filters.active.options.inactive',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col span={24} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.personal.sexType',
            })}
            name="gender"
          >
            <Select
              allowClear
              style={{ width: '100' }}
              options={[
                {
                  value: 'Masculino',
                  label: intl.formatMessage({
                    id: 'employee.personal.genderType.man',
                  }),
                },
                {
                  value: 'Feminino',
                  label: intl.formatMessage({
                    id: 'employee.personal.genderType.woman',
                  }),
                },
              ]}
            ></Select>
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={8}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.personal.form.uf',
            })}
            name="uf"
          >
            <Select
              allowClear
              showSearch
              loading={state.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={state.list.map((s) => ({
                value: s.code,
                label: s.code,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={16}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.personal.form.city',
            })}
            name="city"
          >
            <Select
              allowClear
              showSearch
              loading={city.isFetching}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={city.list.map((c) => ({
                value: c.name,
                label: c.name,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={24}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.personal.form.companies',
            })}
            name="companiesIds"
          >
            <CompaniesInfiniteSelectField
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.professional.form.areas',
            })}
            name="area"
          >
            <Select
              mode="multiple"
              allowClear
              showSearch
              loading={isFetchingAreas}
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label as string)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
              options={areas.map((area) => ({
                value: area.name,
                label: area.name,
              }))}
              style={{ width: '100%' }}
            />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.professional.form.role',
            })}
            name="functionIds"
          >
            <FunctionsSelect />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.professional.form.salary',
            })}
            name="fromSalary"
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12} md={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'employee.modal.report.form.upUntil',
            })}
            name="toSalary"
          >
            <InputDecimal />
          </FloatingFormItem>
        </Col>

        <Col xs={24} sm={12}>
          <Form.Item name="birthday">
            <RangePicker
              size="large"
              format={specificDate ? 'DD/MM/YYYY' : 'DD/MM'}
              key={Number(specificDate)}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={12}>
          <FloatingFormItem
            name="specificDate"
            valuePropName="checked"
            style={{ marginTop: '0.5em' }}
            initialValue={false}
          >
            <Checkbox>
              {intl.formatMessage({
                id: 'employee.modal.report.form.specificDate',
              })}
            </Checkbox>
          </FloatingFormItem>
        </Col>
      </Row>
    </Form>
  );
}

export default EmployeeReportForm;
